import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { createEditor, $getRoot, $getSelection, $isRangeSelection, $isTextNode, $createParagraphNode, $createTextNode, $insertNodes, FORMAT_TEXT_COMMAND, INDENT_CONTENT_COMMAND, OUTDENT_CONTENT_COMMAND } from "lexical"
import { registerRichText, HeadingNode, $createHeadingNode, QuoteNode, $createQuoteNode, $isHeadingNode, $isQuoteNode } from "@lexical/rich-text"
import { $convertFromMarkdownString, $convertToMarkdownString, TRANSFORMERS, registerMarkdownShortcuts } from "@lexical/markdown"
import { LinkNode, $createLinkNode, $toggleLink, $isLinkNode } from "@lexical/link"
import { $setBlocksType } from "@lexical/selection"
import { ListNode, ListItemNode, $isListNode, INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_CHECK_LIST_COMMAND, REMOVE_LIST_COMMAND, registerList, registerCheckList } from "@lexical/list"
import { CodeNode, $isCodeNode } from "@lexical/code"
import { createEmptyHistoryState, registerHistory } from "@lexical/history"
import { showLexicalAiModal } from "../src/noe/lexical_ai_modal"
import { collapseMarkdown } from "../src/lexical_markdown_utils"
import { ImageNode, $createImageNode, IMAGE_TRANSFORMER, setImageResolveParams } from "../src/lexical/ImageNode"

// Rozszerzony zestaw transformerów — IMAGE musi być PRZED LINK (z TRANSFORMERS),
// aby ![alt](url) nie został błędnie zinterpretowany jako link [alt](url)
const ALL_TRANSFORMERS = [IMAGE_TRANSFORMER, ...TRANSFORMERS]

const BTN_CLASS = "p-1.5 rounded hover:bg-gray-200 cursor-pointer text-gray-600 transition-colors"
const ACTIVE_BTN_CLASS = "p-1.5 rounded bg-blue-100 text-blue-700 cursor-pointer transition-colors"

// Lexical WYSIWYG markdown editor jako Stimulus controller
// Toolbar renderowany server-side przez Intum::LexicalEditorComponent
// Connects to data-controller="lexical-markdown"
export default class extends Controller {
  static targets = ["editor", "hidden", "toolbar", "overlay", "imagesField", "overflowBtn", "overflowMenu", "overflowWrapper"]
  static values = { autofocus: { type: Boolean, default: false }, aiContext: { type: String, default: "" }, aiPlaceholder: { type: String, default: "" }, aiPromptLabel: { type: String, default: "" }, defaultMode: { type: String, default: "rich" } }

  connect() {
    this._sourceMode = false
    this._setupImageResolveParams()
    this._bindToolbar()
    this._injectEditorStyles()
    this._initEditor()
    this._setupImageUpload()
    this._setupFormSubmitCleanup()
    this._setupExternalContentListener()
    this._setupToolbarOverflow()
    if (this.defaultModeValue === "source") {
      this._toggleSourceView()
    }
    // jeśli edytor nie ma autofocus, przywróć focus na element z autofocus w formularzu
    if (!this.autofocusValue) {
      const autofocusEl = this.element.closest("form")?.querySelector("[autofocus]")
      if (autofocusEl) requestAnimationFrame(() => autofocusEl.focus())
    }

    if (this.autofocusValue) {
      setTimeout(() => {
        if (this._sourceMode && this._sourceTextarea) {
          this._sourceTextarea.focus()
          this._sourceTextarea.selectionStart = this._sourceTextarea.selectionEnd = 0
        } else {
          this.editorTarget.focus()
          this._editor.update(() => {
            const root = $getRoot()
            const firstChild = root.getFirstDescendant()
            if (firstChild) firstChild.select(0, 0)
          })
        }
      }, 100)
    }
  }

  disconnect() {
    if (this._editor) {
      this._removeUpdateListener?.()
      this._editor = null
    }
    this._styleEl?.remove()
    this._sourceTextarea?.remove()
    this._linkModal?.remove()
    this._imageModal?.remove()
    this._aiModal?.remove()
    if (this._form && this._formDataHandler) {
      this._form.removeEventListener("formdata", this._formDataHandler)
    }
    if (this._setContentHandler) {
      this.element.removeEventListener("lexical:set-content", this._setContentHandler)
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
    if (this._overflowCloseHandler) {
      document.removeEventListener("mousedown", this._overflowCloseHandler)
    }
  }

  _injectEditorStyles() {
    // nadpisanie marginesów paragrafów z prose (prose dodaje duże margin-top/bottom na <p>)
    const style = document.createElement("style")
    style.textContent = `
      [data-lexical-markdown-target="editor"] p { margin-top: 0 !important; margin-bottom: 0 !important; }
      [data-lexical-markdown-target="editor"] li[role="checkbox"] {
        list-style: none; position: relative; margin-left: -0.5rem; padding-left: 1.5rem; cursor: pointer;
      }
      [data-lexical-markdown-target="editor"] li[role="checkbox"]::before {
        content: ''; width: 14px; height: 14px; border: 2px solid #d1d5db; border-radius: 3px;
        position: absolute; left: -2px; top: 3px; cursor: pointer; background: white;
      }
      [data-lexical-markdown-target="editor"] li[role="checkbox"][aria-checked="true"]::before {
        background: #3b82f6; border-color: #3b82f6;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='white'%3E%3Cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2.5-2.5a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3E%3C/svg%3E");
        background-size: 10px; background-position: center; background-repeat: no-repeat;
      }
      [data-lexical-markdown-target="editor"] li[role="checkbox"][aria-checked="true"] span {
        text-decoration: line-through; color: #9ca3af;
      }
    `
    this.editorTarget.parentNode.insertBefore(style, this.editorTarget)
    this._styleEl = style
  }

  // podpięcie akcji do istniejących przycisków toolbara (renderowanych w ERB)
  _bindToolbar() {
    const actions = {
      bold: () => this._formatBold(),
      italic: () => this._formatItalic(),
      strikethrough: () => this._formatStrikethrough(),
      clearFormatting: () => this._clearFormatting(),
      h1: () => this._formatHeading("h1"),
      h2: () => this._formatHeading("h2"),
      h3: () => this._formatHeading("h3"),
      bulletList: () => this._toggleBulletList(),
      orderedList: () => this._toggleOrderedList(),
      checkList: () => this._toggleCheckList(),
      quote: () => this._toggleQuote(),
      code: () => this._toggleCodeBlock(),
      inlineCode: () => this._formatInlineCode(),
      link: () => this._insertLink(),
      image: () => this._showImageModal(),
      source: () => this._toggleSourceView(),
    }

    this._toolbarButtons = {}

    this.toolbarTarget.querySelectorAll("[data-btn]").forEach((btn) => {
      const key = btn.dataset.btn
      const action = actions[key]
      if (!action) return

      btn.addEventListener("mousedown", (e) => {
        e.preventDefault()
        action()
      })
      this._toolbarButtons[key] = { el: btn, btnClass: BTN_CLASS, activeBtnClass: ACTIVE_BTN_CLASS }
    })

    // Obsługa przycisków AI dropdown menu
    this.toolbarTarget.querySelectorAll("[data-ai-mode]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const mode = e.currentTarget.dataset.aiMode
        this._showAiModal(mode)
      })
    })
  }

  _setButtonActive(key, active) {
    const entry = this._toolbarButtons[key]
    if (!entry) return
    // aktualizuj klasę na oryginalnym przycisku i na klonie w overflow menu
    entry.el.className = active ? entry.activeBtnClass : entry.btnClass
    const clone = this._overflowClones?.get(entry.el)
    if (clone) clone.className = active ? entry.activeBtnClass : entry.btnClass
  }

  // toolbar overflow — chowa przyciski które nie mieszczą się w jednej linii i przenosi je do dropdown
  _setupToolbarOverflow() {
    if (!this.hasOverflowBtnTarget || !this.hasOverflowMenuTarget) return

    this._overflowClones = new Map()
    this._overflowMenuOpen = false

    // zamknij overflow menu po kliknięciu poza nim
    this._overflowCloseHandler = (e) => {
      if (!this._overflowMenuOpen) return
      if (this.hasOverflowWrapperTarget && this.overflowWrapperTarget.contains(e.target)) return
      if (this.hasOverflowMenuTarget && this.overflowMenuTarget.contains(e.target)) return
      this._closeOverflowMenu()
    }
    document.addEventListener("mousedown", this._overflowCloseHandler)

    // toggle overflow menu
    this.overflowBtnTarget.addEventListener("mousedown", (e) => {
      e.preventDefault()
      if (this._overflowMenuOpen) {
        this._closeOverflowMenu()
      } else {
        this._openOverflowMenu()
      }
    })

    this._resizeObserver = new ResizeObserver(() => {
      this._handleToolbarOverflow()
    })
    this._resizeObserver.observe(this.toolbarTarget)

    // inicjalny check
    requestAnimationFrame(() => this._handleToolbarOverflow())
  }

  _handleToolbarOverflow() {
    if (!this.hasOverflowBtnTarget) return

    const toolbar = this.toolbarTarget
    const overflowBtn = this.overflowBtnTarget
    const overflowWrapper = this.overflowWrapperTarget
    const overflowMenu = this.overflowMenuTarget

    // pokaż tymczasowo wszystkie przyciski żeby zmierzyć
    const buttons = Array.from(toolbar.querySelectorAll(":scope > button[data-btn], :scope > div[data-controller='dropdown']"))
    buttons.forEach(btn => { btn.style.display = ""; btn.classList.remove("!hidden") })

    // pokaż overflow wrapper (żeby zarezerwować jego miejsce)
    overflowWrapper.style.display = ""
    overflowWrapper.classList.remove("hidden")
    overflowBtn.classList.remove("hidden")

    // sprawdź czy jest overflow (scrollWidth > clientWidth)
    const hiddenButtons = []
    if (toolbar.scrollWidth > toolbar.clientWidth) {
      // chowaj przyciski od końca aż się zmieszczą (pomijaj overflow wrapper)
      for (let i = buttons.length - 1; i >= 0; i--) {
        buttons[i].style.display = "none"
        hiddenButtons.unshift(buttons[i])
        if (toolbar.scrollWidth <= toolbar.clientWidth) break
      }
    }

    // wyczyść overflow menu i stwórz klony
    overflowMenu.innerHTML = ""
    this._overflowClones = new Map()

    if (hiddenButtons.length > 0) {
      hiddenButtons.forEach(btn => {
        // dla AI dropdown — kopiujemy sam przycisk AI, nie cały dropdown
        if (btn.dataset.controller === "dropdown") {
          const aiBtn = btn.querySelector("[data-btn='ai']")
          if (aiBtn) {
            const clone = aiBtn.cloneNode(true)
            clone.className = BTN_CLASS
            clone.removeAttribute("data-action")
            clone.addEventListener("mousedown", (e) => {
              e.preventDefault()
              this._closeOverflowMenu()
              aiBtn.click()
            })
            overflowMenu.appendChild(clone)
          }
          return
        }

        const clone = btn.cloneNode(true)
        clone.style.display = ""
        clone.className = btn.className.replace("!hidden", "").trim()
        clone.addEventListener("mousedown", (e) => {
          e.preventDefault()
          this._closeOverflowMenu()
          btn.dispatchEvent(new MouseEvent("mousedown", { bubbles: true, cancelable: true }))
        })
        this._overflowClones.set(btn, clone)
        overflowMenu.appendChild(clone)
      })
    } else {
      // wszystko mieści się — ukryj overflow
      overflowWrapper.style.display = "none"
    }

    this._closeOverflowMenu()
  }

  _openOverflowMenu() {
    this._overflowMenuOpen = true
    // pozycjonuj menu pod przyciskiem ⋯
    const btnRect = this.overflowBtnTarget.getBoundingClientRect()
    const parentRect = this.element.getBoundingClientRect()
    this.overflowMenuTarget.style.top = `${btnRect.bottom - parentRect.top + 2}px`
    this.overflowMenuTarget.classList.remove("hidden")
  }

  _closeOverflowMenu() {
    this._overflowMenuOpen = false
    if (this.hasOverflowMenuTarget) {
      this.overflowMenuTarget.classList.add("hidden")
    }
  }

  _initEditor() {
    const initialMarkdown = this.hiddenTarget.value || ""

    this._editor = createEditor({
      namespace: "LexicalMarkdownEditor",
      nodes: [HeadingNode, QuoteNode, LinkNode, ListNode, ListItemNode, CodeNode, ImageNode],
      theme: {
        text: {
          bold: "font-bold",
          italic: "italic",
          strikethrough: "line-through",
        },
        heading: {
          h1: "text-2xl font-bold mt-1 mb-px leading-tight",
          h2: "text-xl font-bold mt-0.5 mb-px leading-tight",
          h3: "text-lg font-bold mt-0.5 mb-px leading-tight",
        },
        link: "text-blue-600 underline cursor-pointer",
        paragraph: "mb-px leading-snug",
        quote: "border-l-4 border-gray-300 pl-3 italic text-gray-600 my-0.5",
        list: {
          ul: "list-disc pl-6 mb-px",
          ol: "list-decimal pl-6 mb-px",
          listitem: "mb-0 leading-snug",
          nested: {
            listitem: "list-none",
          },
        },
        code: "bg-gray-100 rounded p-2 font-mono text-sm my-0.5 block",
      },
      onError: (error) => {
        console.error("Lexical error:", error)
      },
    })

    this._editor.setRootElement(this.editorTarget)
    registerRichText(this._editor)
    registerList(this._editor)
    registerCheckList(this._editor)
    registerHistory(this._editor, createEmptyHistoryState(), 300)
    registerMarkdownShortcuts(this._editor, ALL_TRANSFORMERS)

    // załaduj istniejący markdown (shouldPreserveNewLines=true zachowuje puste paragrafy)
    if (initialMarkdown.trim().length > 0) {
      this._editor.update(() => {
        $convertFromMarkdownString(initialMarkdown, ALL_TRANSFORMERS, undefined, true)
      })
    }

    // synchronizuj markdown do ukrytego pola
    this._removeUpdateListener = this._editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const markdown = $convertToMarkdownString(ALL_TRANSFORMERS, undefined, true)
        this.hiddenTarget.value = collapseMarkdown(markdown)
        this._updateToolbarState()
      })
    })

    // skróty klawiszowe (rich mode)
    // Ctrl+B, Ctrl+I — obsługiwane natywnie przez Lexical
    this.editorTarget.addEventListener("keydown", (e) => {
      this._handleKeyboardShortcut(e)
    })
  }

  _updateToolbarState() {
    if (!this._editor) return

    this._editor.getEditorState().read(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return

      this._setButtonActive("bold", selection.hasFormat("bold"))
      this._setButtonActive("italic", selection.hasFormat("italic"))
      this._setButtonActive("strikethrough", selection.hasFormat("strikethrough"))

      const anchorNode = selection.anchor.getNode()
      const element = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow()

      this._setButtonActive("h1", $isHeadingNode(element) && element.getTag() === "h1")
      this._setButtonActive("h2", $isHeadingNode(element) && element.getTag() === "h2")
      this._setButtonActive("h3", $isHeadingNode(element) && element.getTag() === "h3")
      this._setButtonActive("quote", $isQuoteNode(element))
      this._setButtonActive("code", $isCodeNode(element))

      const parent = anchorNode.getParent()
      const grandparent = parent?.getParent()
      const isBullet = $isListNode(grandparent) && grandparent.getListType() === "bullet"
      const isOrdered = $isListNode(grandparent) && grandparent.getListType() === "number"
      const isCheck = $isListNode(grandparent) && grandparent.getListType() === "check"
      this._setButtonActive("bulletList", isBullet)
      this._setButtonActive("orderedList", isOrdered)
      this._setButtonActive("checkList", isCheck)

      const isLink = $isLinkNode(parent) || $isLinkNode(anchorNode)
      this._setButtonActive("link", isLink)
    })
  }

  // --- formatowanie tekstu ---

  _formatBold() {
    if (this._sourceMode) { this._wrapSourceSelection("**", "**"); return }
    this._editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")
  }

  _formatItalic() {
    if (this._sourceMode) { this._wrapSourceSelection("*", "*"); return }
    this._editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")
  }

  _formatStrikethrough() {
    if (this._sourceMode) { this._wrapSourceSelection("~~", "~~"); return }
    this._editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")
  }

  _clearFormatting() {
    this._editor.update(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return
      // usuń formatowanie inline (bold, italic, strikethrough itp.) z zaznaczonych text node'ów
      selection.getNodes().forEach((node) => {
        if ($isTextNode(node)) {
          node.setFormat(0)
        }
      })
      // jeśli kursor w bloku (heading, quote, code) — zamień na paragraf
      const anchorNode = selection.anchor.getNode()
      const element = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow()
      if ($isHeadingNode(element) || $isQuoteNode(element) || $isCodeNode(element)) {
        $setBlocksType(selection, () => $createParagraphNode())
      }
    })
  }

  _formatHeading(tag) {
    if (this._sourceMode) {
      const prefixMap = { h1: "# ", h2: "## ", h3: "### " }
      this._prefixSourceLine(prefixMap[tag])
      return
    }
    this._editor.update(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return
      const anchorNode = selection.anchor.getNode()
      const element = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow()

      if ($isHeadingNode(element) && element.getTag() === tag) {
        $setBlocksType(selection, () => $createParagraphNode())
      } else {
        $setBlocksType(selection, () => $createHeadingNode(tag))
      }
    })
  }

  _toggleBulletList() {
    if (this._sourceMode) { this._prefixSourceLine("- "); return }
    this._editor.getEditorState().read(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return
      const grandparent = selection.anchor.getNode().getParent()?.getParent()
      if ($isListNode(grandparent) && grandparent.getListType() === "bullet") {
        this._editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
      } else {
        this._editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
      }
    })
  }

  _toggleOrderedList() {
    if (this._sourceMode) { this._prefixSourceLine("1. "); return }
    this._editor.getEditorState().read(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return
      const grandparent = selection.anchor.getNode().getParent()?.getParent()
      if ($isListNode(grandparent) && grandparent.getListType() === "number") {
        this._editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
      } else {
        this._editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
      }
    })
  }

  _toggleCheckList() {
    if (this._sourceMode) { this._prefixSourceLine("- [ ] "); return }
    this._editor.getEditorState().read(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return
      const grandparent = selection.anchor.getNode().getParent()?.getParent()
      if ($isListNode(grandparent) && grandparent.getListType() === "check") {
        this._editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
      } else {
        this._editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)
      }
    })
  }

  _toggleQuote() {
    if (this._sourceMode) { this._prefixSourceLine("> "); return }
    this._editor.update(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return
      const anchorNode = selection.anchor.getNode()
      const element = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow()
      if ($isQuoteNode(element)) {
        $setBlocksType(selection, () => $createParagraphNode())
      } else {
        $setBlocksType(selection, () => $createQuoteNode())
      }
    })
  }

  _formatInlineCode() {
    if (this._sourceMode) { this._wrapSourceSelection("`", "`"); return }
    this._editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code")
  }

  _toggleCodeBlock() {
    if (this._sourceMode) { this._wrapSourceSelection("```\n", "\n```"); return }
    this._editor.update(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return
      const anchorNode = selection.anchor.getNode()
      const element = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow()
      if ($isCodeNode(element)) {
        $setBlocksType(selection, () => $createParagraphNode())
      } else {
        $setBlocksType(selection, () => new CodeNode())
      }
    })
  }

  // --- link ---

  _insertLink() {
    if (this._sourceMode) {
      const ta = this._sourceTextarea
      const selected = ta ? ta.value.substring(ta.selectionStart, ta.selectionEnd) : ""
      this._showLinkModal(selected, "")
      return
    }

    let isLink = false
    let selectedText = ""
    let existingUrl = ""

    this._editor.getEditorState().read(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return
      selectedText = selection.getTextContent()

      const linkNode = selection.getNodes().find((node) => $isLinkNode(node.getParent()))
      if (linkNode) {
        const parent = linkNode.getParent()
        if ($isLinkNode(parent)) {
          isLink = true
          existingUrl = parent.getURL()
        }
      }
    })

    if (isLink) {
      this._editor.update(() => { $toggleLink(null) })
      return
    }

    this._showLinkModal(selectedText, existingUrl)
  }

  _showLinkModal(selectedText, existingUrl) {
    this._linkModal?.remove()

    const inputClass = "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"

    const modal = document.createElement("div")
    modal.className = "relative z-50"
    modal.innerHTML = `
      <div class="fixed inset-0 bg-gray-500/75 transition-opacity"></div>
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
            <h3 class="text-lg font-semibold leading-6 text-gray-900 mb-4">Link</h3>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <input type="url" data-link-field="url" placeholder="https://..."
                  value="${existingUrl}"
                  class="${inputClass}">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Nazwa</label>
                <input type="text" data-link-field="name" placeholder="Tekst linku (opcjonalnie)"
                  value="${selectedText.replace(/"/g, "&quot;")}"
                  class="${inputClass}">
              </div>
            </div>
            <div class="mt-4 flex justify-end gap-2">
              <button type="button" data-link-action="cancel"
                class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 cursor-pointer">
                Anuluj
              </button>
              <button type="button" data-link-action="confirm"
                class="rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 cursor-pointer">
                Dodaj
              </button>
            </div>
          </div>
        </div>
      </div>
    `

    document.body.appendChild(modal)
    this._linkModal = modal

    const urlInput = modal.querySelector("[data-link-field='url']")
    const nameInput = modal.querySelector("[data-link-field='name']")

    urlInput.focus()

    const close = () => {
      modal.remove()
      this._linkModal = null
      if (this._sourceMode) {
        this._sourceTextarea?.focus()
      } else {
        this.editorTarget.focus()
      }
    }

    const confirm = () => {
      const url = urlInput.value.trim()
      if (!url) { close(); return }
      const name = nameInput.value.trim()

      if (this._sourceMode) {
        const ta = this._sourceTextarea
        if (ta) {
          const start = ta.selectionStart
          const end = ta.selectionEnd
          const text = ta.value
          const linkText = name || text.substring(start, end) || url
          const markdown = `[${linkText}](${url})`
          ta.value = text.substring(0, start) + markdown + text.substring(end)
          ta.selectionStart = start + markdown.length
          ta.selectionEnd = start + markdown.length
          ta.dispatchEvent(new Event("input"))
        }
        close()
        return
      }

      this._editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection) && !selection.isCollapsed()) {
          $toggleLink(url)
        } else {
          const linkNode = $createLinkNode(url)
          linkNode.append($createTextNode(name || url))
          $insertNodes([linkNode])
        }
      })
      close()
    }

    modal.querySelector("[data-link-action='confirm']").addEventListener("click", confirm)
    modal.querySelector("[data-link-action='cancel']").addEventListener("click", close)

    const onKeydown = (e) => {
      if (e.key === "Escape") close()
      if (e.key === "Enter") { e.preventDefault(); confirm() }
    }
    urlInput.addEventListener("keydown", onKeydown)
    nameInput.addEventListener("keydown", onKeydown)
    modal.querySelector(".fixed.inset-0").addEventListener("click", close)
  }

  // --- AI modal ---

  _showAiModal(mode = "creative") {
    // Dla spelling i polish — automatyczna korekta bez modala
    if (mode === "spelling" || mode === "polish") {
      this._applyAiDirectly(mode)
      return
    }

    // Dla creative — pokaż modal z promptem
    this._aiModal?.remove()
    this._aiModal = showLexicalAiModal({
      aiContext: this.aiContextValue,
      aiMode: mode,
      placeholder: this.aiPlaceholderValue || undefined,
      promptLabel: this.aiPromptLabelValue || undefined,
      onUseResponse: (content) => {
        this.element.dispatchEvent(new CustomEvent("lexical:set-content", { detail: { markdown: content } }))
      },
      onClose: () => {
        this._aiModal = null
        this.editorTarget.focus()
      },
    })
  }

  async _applyAiDirectly(mode) {
    // Pobierz aktualną zawartość edytora
    const currentContent = this.hiddenTarget.value || ""
    if (!currentContent.trim()) {
      alert("Edytor jest pusty — nie ma czego poprawić")
      return
    }

    const csrfToken = document.querySelector("meta[name='csrf-token']").content

    // Stwórz i pokaż spinner overlay
    const overlay = document.createElement("div")
    overlay.className = "absolute inset-0 bg-white/80 flex items-center justify-center rounded-md z-10"
    overlay.innerHTML = `
      <div class="flex flex-col items-center gap-3">
        <div class="w-10 h-10 rounded-full animate-spin border-4 border-solid border-blue-500 border-t-transparent"></div>
        <div class="text-sm text-gray-600 font-medium">AI poprawia tekst...</div>
      </div>
    `
    this.element.appendChild(overlay)

    try {
      // Mapowanie trybu na system prompt
      // Dla spelling/polish zawsze używamy dedykowanych promptów, ignorując aiContextValue
      const aiModeToPrompt = {
        spelling: "spelling_editor",
        polish: "polish_editor"
      }
      const systemPrompt = aiModeToPrompt[mode]

      // Utwórz konwersację
      const createRes = await fetch("/noe/conversations/create_for_ai.json", {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken, "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ ai_context: systemPrompt }),
      })
      const createData = await createRes.json()
      if (!createRes.ok) {
        alert(createData.error || "Błąd tworzenia konwersacji AI")
        return
      }

      // Wyślij zawartość edytora jako prompt
      const sendRes = await fetch(createData.send_message_url, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken, "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ content: currentContent }),
      })
      const sendData = await sendRes.json()
      if (!sendRes.ok) {
        alert(sendData.error || "Błąd generowania odpowiedzi AI")
        return
      }

      // Wstaw odpowiedź AI do edytora
      const aiContent = sendData.assistant_message.content
      this.element.dispatchEvent(new CustomEvent("lexical:set-content", { detail: { markdown: aiContent } }))
    } catch (error) {
      console.error("AI error:", error)
      alert("Błąd połączenia z serwerem")
    } finally {
      // Usuń spinner overlay
      overlay.remove()
      this.editorTarget.focus()
    }
  }

  // --- form submit: usunięcie \r ---

  _setupFormSubmitCleanup() {
    this._form = this.element.closest("form")
    if (!this._form) return

    this._formDataHandler = (e) => {
      const name = this.hiddenTarget.name
      const val = e.formData.get(name)
      if (val) e.formData.set(name, val.replace(/\r/g, ""))
    }
    this._form.addEventListener("formdata", this._formDataHandler)
  }

  // --- image resolve params (attachable_type/id dla wydajniejszego wyszukania) ---

  _setupImageResolveParams() {
    const parts = []
    if (this.element.dataset.attachableType) parts.push(`attachable_type=${encodeURIComponent(this.element.dataset.attachableType)}`)
    if (this.element.dataset.attachableId) parts.push(`attachable_id=${this.element.dataset.attachableId}`)
    setImageResolveParams(parts.length ? `${parts.join("&")}&` : "")
  }

  // --- image upload (opcjonalny) ---

  _setupImageUpload() {
    if (!this.element.dataset.directUploadUrl) return

    this.editorTarget.addEventListener("drop", (e) => {
      const files = e.dataTransfer?.files
      if (!files?.length) return
      const imageFiles = Array.from(files).filter((f) => f.type.startsWith("image/"))
      if (!imageFiles.length) return
      e.preventDefault()
      imageFiles.forEach((file) => this._uploadAndInsertImage(file, ""))
    })

    this.editorTarget.addEventListener("paste", (e) => {
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault()
          this._uploadAndInsertImage(item.getAsFile(), "")
          return
        }
      }
    })
  }

  _showImageModal() {
    this._imageModal?.remove()

    const inputClass = "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"

    const modal = document.createElement("div")
    modal.className = "relative z-50"
    modal.innerHTML = `
      <div class="fixed inset-0 bg-gray-500/75 transition-opacity"></div>
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
            <h3 class="text-lg font-semibold leading-6 text-gray-900 mb-4">Obraz</h3>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Upload</label>
                <input type="file" accept="image/*" data-img-field="file"
                  class="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 file:cursor-pointer cursor-pointer">
              </div>
              <div class="flex items-center gap-3">
                <div class="flex-1 h-px bg-gray-200"></div>
                <span class="text-xs text-gray-400">lub</span>
                <div class="flex-1 h-px bg-gray-200"></div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">URL obrazka</label>
                <input type="url" data-img-field="url" placeholder="https://..."
                  class="${inputClass}">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Opis (opcjonalnie)</label>
                <input type="text" data-img-field="alt" placeholder="Tekst alternatywny"
                  class="${inputClass}">
              </div>
            </div>
            <div class="mt-4 flex justify-end gap-2">
              <button type="button" data-img-action="cancel"
                class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 cursor-pointer">
                Anuluj
              </button>
              <button type="button" data-img-action="confirm"
                class="rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 cursor-pointer">
                Dodaj
              </button>
            </div>
          </div>
        </div>
      </div>
    `

    document.body.appendChild(modal)
    this._imageModal = modal

    const fileInput = modal.querySelector("[data-img-field='file']")
    const urlInput = modal.querySelector("[data-img-field='url']")
    const altInput = modal.querySelector("[data-img-field='alt']")
    const fileBlock = fileInput.closest("div")
    const urlBlock = urlInput.closest("div")
    const separator = modal.querySelector("[data-img-field='file']").closest("div").nextElementSibling

    fileInput.addEventListener("change", () => {
      if (fileInput.files?.length) {
        urlBlock.classList.add("hidden")
        separator.classList.add("hidden")
        altInput.focus()
      } else {
        urlBlock.classList.remove("hidden")
        separator.classList.remove("hidden")
      }
    })

    urlInput.addEventListener("input", () => {
      if (urlInput.value.trim()) {
        fileBlock.classList.add("hidden")
        separator.classList.add("hidden")
      } else {
        fileBlock.classList.remove("hidden")
        separator.classList.remove("hidden")
      }
    })

    const close = () => {
      modal.remove()
      this._imageModal = null
      if (this._imageModalKeydown) document.removeEventListener("keydown", this._imageModalKeydown)
      if (this._sourceMode && this._sourceTextarea) {
        this._sourceTextarea.focus()
      } else {
        this.editorTarget.focus()
      }
    }

    const confirm = async () => {
      const alt = altInput.value.trim()
      const file = fileInput.files?.[0]
      const url = urlInput.value.trim()
      if (!file && !url) { close(); return }
      close()
      if (file) {
        await this._uploadAndInsertImage(file, alt)
      } else {
        this._insertImageMarkdown(url, alt)
      }
    }

    modal.querySelector("[data-img-action='confirm']").addEventListener("click", confirm)
    modal.querySelector("[data-img-action='cancel']").addEventListener("click", close)

    const onKeydown = (e) => {
      if (e.key === "Escape") close()
      if (e.key === "Enter" && e.target !== fileInput) { e.preventDefault(); confirm() }
    }
    document.addEventListener("keydown", onKeydown)
    this._imageModalKeydown = onKeydown
    modal.querySelector(".fixed.inset-0").addEventListener("click", close)
  }

  _insertImageMarkdown(src, alt) {
    if (this._sourceMode && this._sourceTextarea) {
      const md = `![${alt || ""}](${src})`
      const ta = this._sourceTextarea
      const start = ta.selectionStart
      ta.value = ta.value.slice(0, start) + md + ta.value.slice(ta.selectionEnd)
      ta.selectionStart = ta.selectionEnd = start + md.length
      ta.dispatchEvent(new Event("input"))
      ta.focus()
    } else {
      this._editor.update(() => {
        $insertNodes([$createImageNode(src, alt || "")])
      })
    }
  }

  async _uploadAndInsertImage(file, alt) {
    if (this.hasOverlayTarget) this.overlayTarget.classList.remove("hidden")

    try {
      const uploadPath = `${this.element.dataset.directUploadUrl}?extended=true&in_text=true`
      const blob = await new Promise((resolve, reject) => {
        new DirectUpload(file, uploadPath).create((error, b) => {
          if (error) reject(error)
          else resolve(b)
        })
      })

      if (this.hasImagesFieldTarget && blob.signed_id) {
        const current = this.imagesFieldTarget.value
        this.imagesFieldTarget.value = current ? `${current},${blob.signed_id}` : blob.signed_id
      }

      // blob.filename = unikalna nazwa z ExtendedAttachment (np. 78701e9a76d9-gmail.png)
      this._insertImageMarkdown(blob.filename, alt)
    } catch (error) {
      console.error("Image upload error:", error)
    } finally {
      if (this.hasOverlayTarget) this.overlayTarget.classList.add("hidden")
    }
  }

  // --- zewnętrzne ustawianie treści (np. wstawienie szablonu) ---

  _setupExternalContentListener() {
    this._setContentHandler = (e) => {
      const markdown = e.detail?.markdown || ""
      this.hiddenTarget.value = markdown
      if (this._sourceMode && this._sourceTextarea) {
        this._sourceTextarea.value = markdown
      }
      this._editor.update(() => {
        $convertFromMarkdownString(markdown, ALL_TRANSFORMERS, undefined, true)
      })
    }
    this.element.addEventListener("lexical:set-content", this._setContentHandler)
  }

  // --- newline konwersja ---
  // Format storage: \n = paragraf, \n\n = celowa pusta linia
  // Import: $convertFromMarkdownString z shouldPreserveNewLines=true zachowuje puste paragrafy
  // Export: $convertToMarkdownString z shouldPreserveNewLines=true + collapseMarkdown (unescape \_)

  // --- source view helpery ---

  // Owija zaznaczony tekst w textarea markerami markdown (np. **bold**)
  _wrapSourceSelection(before, after) {
    const ta = this._sourceTextarea
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const text = ta.value
    const selected = text.substring(start, end)

    // toggle — jeśli zaznaczenie już owinięte, usuń markery
    const bLen = before.length
    const aLen = after.length
    if (start >= bLen && text.substring(start - bLen, start) === before && text.substring(end, end + aLen) === after) {
      ta.value = text.substring(0, start - bLen) + selected + text.substring(end + aLen)
      ta.selectionStart = start - bLen
      ta.selectionEnd = end - bLen
    } else {
      ta.value = text.substring(0, start) + before + selected + after + text.substring(end)
      ta.selectionStart = start + bLen
      ta.selectionEnd = end + bLen
    }
    ta.dispatchEvent(new Event("input"))
    ta.focus()
  }

  // Toggle prefix na początku aktualnej linii (np. "# ", "- ")
  _prefixSourceLine(prefix) {
    const ta = this._sourceTextarea
    if (!ta) return
    const start = ta.selectionStart
    const text = ta.value

    // znajdź początek linii
    const lineStart = text.lastIndexOf("\n", start - 1) + 1
    const lineEnd = text.indexOf("\n", start)
    const end = lineEnd === -1 ? text.length : lineEnd

    const line = text.substring(lineStart, end)

    if (line.startsWith(prefix)) {
      // usuń prefix
      ta.value = text.substring(0, lineStart) + line.substring(prefix.length) + text.substring(end)
      ta.selectionStart = Math.max(lineStart, start - prefix.length)
      ta.selectionEnd = ta.selectionStart
    } else {
      // dodaj prefix (usuń inne headingi jeśli to heading)
      let cleanLine = line
      if (prefix.match(/^#{1,3} $/)) {
        cleanLine = line.replace(/^#{1,6} /, "")
      }
      ta.value = text.substring(0, lineStart) + prefix + cleanLine + text.substring(end)
      ta.selectionStart = lineStart + prefix.length + (start - lineStart) + (prefix.length - (line.length - cleanLine.length))
      ta.selectionEnd = ta.selectionStart
    }
    ta.dispatchEvent(new Event("input"))
    ta.focus()
  }

  // --- source view ---

  _toggleSourceView() {
    this._sourceMode = !this._sourceMode

    // zapisz preferencję trybu edytora
    this._saveEditorModePreference(this._sourceMode ? "source" : "rich")

    // przełącz ikonę przycisku source (bez zmiany koloru — tylko ikona się zmienia)
    const sourceBtn = this._toolbarButtons.source?.el
    if (sourceBtn) {
      const toSource = sourceBtn.querySelector("[data-source-icon='toSource']")
      const toRich = sourceBtn.querySelector("[data-source-icon='toRich']")
      if (toSource) toSource.classList.toggle("hidden", !this._sourceMode)
      if (toRich) toRich.classList.toggle("hidden", this._sourceMode)
    }

    // w source mode disabluj tylko clearFormatting (reszta działa na textarea)
    const sourceOnlyDisabled = ["clearFormatting"]
    const setSourceDisabled = (disabled) => {
      sourceOnlyDisabled.forEach((key) => {
        const entry = this._toolbarButtons[key]
        if (!entry) return
        entry.el.disabled = disabled
        entry.el.style.opacity = disabled ? "0.4" : "1"
      })
    }

    if (this._sourceMode) {
      if (!this._sourceTextarea) {
        this._sourceTextarea = document.createElement("textarea")
        this._sourceTextarea.className = "w-full border border-gray-300 rounded-md p-3 text-sm bg-white focus:outline-none focus:border-gray-800 focus:ring-0 resize-none overflow-hidden"
        this._sourceTextarea.style.minHeight = this.editorTarget.style.minHeight || "220px"
        this.editorTarget.parentNode.insertBefore(this._sourceTextarea, this.editorTarget.nextSibling)
        // synchronizuj zmiany w source textarea do ukrytego pola na bieżąco
        this._sourceTextarea.addEventListener("input", () => {
          this.hiddenTarget.value = this._sourceTextarea.value
          this._autoResizeSourceTextarea()
        })
        // skróty klawiszowe (source mode)
        this._sourceTextarea.addEventListener("keydown", (e) => {
          this._handleKeyboardShortcut(e)
        })
        // paste/drop obrazków w source mode
        if (this.element.dataset.directUploadUrl) {
          this._sourceTextarea.addEventListener("drop", (e) => {
            const files = e.dataTransfer?.files
            if (!files?.length) return
            const imageFiles = Array.from(files).filter((f) => f.type.startsWith("image/"))
            if (!imageFiles.length) return
            e.preventDefault()
            imageFiles.forEach((file) => this._uploadAndInsertImage(file, ""))
          })
          this._sourceTextarea.addEventListener("paste", (e) => {
            const items = e.clipboardData?.items
            if (!items) return
            for (const item of items) {
              if (item.type.startsWith("image/")) {
                e.preventDefault()
                this._uploadAndInsertImage(item.getAsFile(), "")
                return
              }
            }
          })
        }
        // kontynuacja list po Enter (- item, 1. item)
        this._sourceTextarea.addEventListener("keydown", (e) => {
          if (e.key !== "Enter") return
          const ta = this._sourceTextarea
          const pos = ta.selectionStart
          const text = ta.value
          const lineStart = text.lastIndexOf("\n", pos - 1) + 1
          const line = text.substring(lineStart, pos)

          // lista numerowana: "1. tekst" → "2. "
          const olMatch = line.match(/^(\s*)(\d+)\.\s(.+)/)
          if (olMatch) {
            e.preventDefault()
            const [, indent, num] = olMatch
            const next = `\n${indent}${parseInt(num) + 1}. `
            ta.value = text.slice(0, pos) + next + text.slice(ta.selectionEnd)
            ta.selectionStart = ta.selectionEnd = pos + next.length
            ta.dispatchEvent(new Event("input"))
            return
          }

          // checklist: "- [ ] tekst" lub "- [x] tekst" → "- [ ] "
          const clMatch = line.match(/^(\s*)[-*]\s\[[ x]\]\s(.+)/)
          if (clMatch) {
            e.preventDefault()
            const [, indent] = clMatch
            const marker = line.trimStart().charAt(0)
            const next = `\n${indent}${marker} [ ] `
            ta.value = text.slice(0, pos) + next + text.slice(ta.selectionEnd)
            ta.selectionStart = ta.selectionEnd = pos + next.length
            ta.dispatchEvent(new Event("input"))
            return
          }

          // lista nienumerowana: "- tekst" → "- "
          const ulMatch = line.match(/^(\s*)[-*]\s(.+)/)
          if (ulMatch) {
            e.preventDefault()
            const [, indent] = ulMatch
            const marker = line.trimStart().charAt(0)
            const next = `\n${indent}${marker} `
            ta.value = text.slice(0, pos) + next + text.slice(ta.selectionEnd)
            ta.selectionStart = ta.selectionEnd = pos + next.length
            ta.dispatchEvent(new Event("input"))
            return
          }

          // pusta linia listy (sam marker bez tekstu) → usuń marker, zakończ listę
          const emptyOl = line.match(/^(\s*)\d+\.\s*$/)
          const emptyUl = line.match(/^(\s*)[-*]\s*$/)
          const emptyCl = line.match(/^(\s*)[-*]\s\[[ x]\]\s*$/)
          if (emptyOl || emptyUl || emptyCl) {
            e.preventDefault()
            ta.value = text.slice(0, lineStart) + "\n" + text.slice(pos)
            ta.selectionStart = ta.selectionEnd = lineStart + 1
            ta.dispatchEvent(new Event("input"))
          }
        })
      }

      this._sourceTextarea.value = this.hiddenTarget.value || ""
      this._sourceTextarea.style.display = "block"
      this.editorTarget.style.display = "none"
      this._autoResizeSourceTextarea()
      setSourceDisabled(true)
      if (this.autofocusValue) {
        this._sourceTextarea.focus()
        this._sourceTextarea.selectionStart = this._sourceTextarea.selectionEnd = 0
      }
    } else {
      const sourceMarkdown = this._sourceTextarea.value
      this._editor.update(() => {
        $convertFromMarkdownString(sourceMarkdown, ALL_TRANSFORMERS, undefined, true)
        const root = $getRoot()
        const firstChild = root.getFirstDescendant()
        if (firstChild) firstChild.select(0, 0)
      })

      this._sourceTextarea.style.display = "none"
      this.editorTarget.style.display = "block"
      setSourceDisabled(false)
      this._editor.focus()
    }
  }

  _handleKeyboardShortcut(e) {
    // Tab / Shift+Tab — wcięcia w listach
    if (e.key === "Tab") {
      e.preventDefault()
      if (this._sourceMode) {
        const ta = this._sourceTextarea
        if (!ta) return
        const start = ta.selectionStart
        const text = ta.value
        const lineStart = text.lastIndexOf("\n", start - 1) + 1
        if (e.shiftKey) {
          const spaces = text.substring(lineStart).match(/^ {1,2}/)?.[0]?.length || 0
          if (spaces > 0) {
            ta.value = text.substring(0, lineStart) + text.substring(lineStart + spaces)
            ta.selectionStart = ta.selectionEnd = Math.max(lineStart, start - spaces)
            ta.dispatchEvent(new Event("input"))
          }
        } else {
          ta.value = text.substring(0, lineStart) + "  " + text.substring(lineStart)
          ta.selectionStart = ta.selectionEnd = start + 2
          ta.dispatchEvent(new Event("input"))
        }
      } else {
        this._editor.dispatchCommand(e.shiftKey ? OUTDENT_CONTENT_COMMAND : INDENT_CONTENT_COMMAND, undefined)
      }
      return
    }

    const mod = e.ctrlKey || e.metaKey
    if (!mod) return

    if (e.shiftKey) {
      switch (e.key.toLowerCase()) {
        case "s": e.preventDefault(); this._formatStrikethrough(); break
        case "c": e.preventDefault(); this._formatInlineCode(); break
        case "h": e.preventDefault(); this._formatHeading("h3"); break
        case "l": e.preventDefault(); this._toggleBulletList(); break
        case "7": e.preventDefault(); this._toggleOrderedList(); break
        case "i": e.preventDefault(); this._showImageModal(); break
      }
    } else {
      switch (e.key.toLowerCase()) {
        case "k": e.preventDefault(); this._insertLink(); break
        case "enter": e.preventDefault(); this.element.closest("form")?.requestSubmit(); break
      }
    }
  }

  _autoResizeSourceTextarea() {
    const ta = this._sourceTextarea
    if (!ta) return
    ta.style.height = "auto"
    ta.style.height = `${ta.scrollHeight}px`
  }

  _saveEditorModePreference(mode) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    if (!csrfToken) return

    fetch("/account/set_field", {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, "Content-Type": "application/x-www-form-urlencoded" },
      credentials: "same-origin",
      body: `target=user_setting&key=intum.markdown_editor.default_mode&value=${mode}`,
    }).catch(() => {})
  }
}
